import type { StoreData } from "@features/stores/types";
import type { MyCartUpdateAction } from "@graphql/generated/types";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { executeCommerceToolsWithRetry } from "@lib/utils/executeCommerceToolsWithRetry";
import { hasGraphQLErrors } from "@lib/utils/graphql/hasGraphQLErrors";
import { buildSupplyChannelActions } from "@lib/utils/checkout-utility/supplyChannelActions";
import type { GraphQLResponse } from "graphql-request";

type ClearInvalidStoreDataParams = {
    storeData: StoreData;
    cartId: string;
    cartVersion: number;
    availableStoreCount: number;
    lineItems: readonly { id: string }[];
    locale: string;
    setStoreData: (data: StoreData | null) => void;
    executeUpdateCart: (params: {
        cartId: string;
        cartVersion: number;
        storeKey: string;
        locale: string;
        actions: MyCartUpdateAction[];
    }) => Promise<GraphQLResponse<unknown>>;
};

/**
 * Clears invalid store data from both the Zustand state and the cart's supply channel.
 * This function should be called when a previously selected store is no longer available.
 * The channel is cleared on every line item, matching how it is set.
 *
 * @param params - The parameters needed to clear the store data
 */
export const clearInvalidStoreData = async ({
    storeData,
    cartId,
    cartVersion,
    availableStoreCount,
    lineItems,
    locale,
    setStoreData,
    executeUpdateCart,
}: ClearInvalidStoreDataParams): Promise<void> => {
    Logger.error(ServiceType.WEB, "Selected store is no longer available", {
        tag: LogTag.CHECKOUT,
        storeId: storeData.storeId,
        cartId,
        availableStoreCount,
    });

    const { errors } = await executeCommerceToolsWithRetry(
        executeUpdateCart,
        {
            cartId,
            cartVersion,
            storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
            locale,
            actions: buildSupplyChannelActions(lineItems, {}),
        },
        0,
        "clearInvalidStoreData supply channel"
    );

    if (hasGraphQLErrors(errors)) {
        Logger.error(ServiceType.WEB, "Failed to clear store supply channel", {
            tag: LogTag.CHECKOUT,
            storeId: storeData.storeId,
            cartId,
            errors,
        });
    }

    // Clear the Zustand store even if the channel unset failed; the intent is to
    // drop the invalid store from the UI regardless.
    setStoreData(null);
};
